function TimeSlotPermutationService(DateTimeUtilitiesService) {
    'ngInject';

    const service = this;

    /***
     * @ngdoc method
     * @name permute
     * @methodOf BB.TimeSlotPermutationService
     * @description
     * Finds all valid sequences using Steinhaus–Johnson–Trotter algorithm
     *
     * http://stackoverflow.com/questions/9960908/permutations-in-javascript
     * http://en.wikipedia.org/wiki/Steinhaus%E2%80%93Johnson%E2%80%93Trotter_algorithm
     * @param {array} inputArray The array of objects you wish to find all permutations for
     */
    service.permute = function (inputArray) {
        const permutations = [];
        const usedInput = [];

        const main = function () {
            let input;
            let loopIndex = 0;

            while (loopIndex < inputArray.length) {
                input = inputArray.splice(loopIndex, 1)[0];
                usedInput.push(input);

                if (inputArray.length === 0) {
                    permutations.push(usedInput.slice());
                }

                main();
                inputArray.splice(loopIndex, 0, input);
                usedInput.pop();
                loopIndex++;
            }

            return permutations;
        };

        return main();
    };

    /***
     * @ngdoc method
     * @name addExistingDateTimes
     * @methodOf BB.TimeSlotPermutationService
     * @description
     * Add date times previously selected to slots
     *
     * @param {array} stackedItems The array of BasketItems that will be checked out
     * @param {array} slots The array of Slot objects that are currently loaded
     */
    service.addExistingDateTimes = function (stackedItems, slots) {

        for (const itemIndex in stackedItems) {
            const item = stackedItems[itemIndex];

            if (slots.length && (item.datetime || (item.date && item.time))) {

                let timeSlot;

                const datetime = item.datetime || DateTimeUtilitiesService.convertTimeToMoment(item.date.date, item.time.time);
                const item_slots = slots[itemIndex];
                const daySlotsForItem = item_slots[datetime.toISODate()];

                if (daySlotsForItem) {

                    timeSlot = _.findWhere(daySlotsForItem, { time: item.time.time });

                    if (!timeSlot) {
                        timeSlot = item.time;
                        daySlotsForItem.splice(0, 0, timeSlot);
                    }

                    // ensure only the first time slot is marked as selected
                    if (item.self === stackedItems[0].self) {
                        timeSlot.selected = true;
                    }
                }
            }
        }
    };

    /***
     * @ngdoc method
     * @name isItemSlotsValid
     * @methodOf BB.TimeSlotPermutationService
     * @description
     * checks the items have a matching slots
     * and sorts and assigns slot times to items if valid
     *
     * @param {array} items The array of BasketItems
     * @param {array} itemSlots An array of array of slots. Where the first set of arrays correspond to the first item
     */
    service.isItemSlotsValid = function (items, itemsSlots) {
        let isSlotsValid = false;

        for (const itemIndex in items) {

            for (const itemSlotIndex in itemsSlots[itemIndex]) {
                if (itemsSlots[itemIndex][itemSlotIndex] && itemsSlots[itemIndex][itemSlotIndex].length !== 0) {
                    isSlotsValid = true;
                }
            }

            if (isSlotsValid) {

                // set item slot by time
                items[itemIndex].slots = {};

                for (const slotIndex in itemsSlots[itemIndex]) {
                    items[itemIndex].slots[slotIndex] = _.indexBy(itemsSlots[itemIndex][slotIndex], 'time');
                }
            }
        }

        return isSlotsValid;
    };

    /***
     * @ngdoc method
     * @name isItemsSlotPermutationValid
     * @methodOf BB.TimeSlotPermutationService
     * @description
     * Checks the slot has been set correctly with the correct time
     * Also sets the slot.next object to point to the next slot in the permutation
     *
     * @param {array} items A array of BasketItems
     * @param {object} slot A Slot object
     * @param {string} daySlot A string representing a date. Used for defining attribute for slots
     * @param {array} permutation An array representing the combination of slots
     */
    service.isItemsSlotPermutationValid = function (items, slot, daySlot, permutation) {
        if (items.length && permutation.length && items[permutation[0]]) {
            const durationOfFirst = items[permutation[0]].service.duration;
            let next = slot.time + durationOfFirst;

            // loop through remaining permutations
            for (let i = 1; i < permutation.length; i++) {
                if (items[permutation[i]] && items[permutation[i]].slots && items[permutation[i]].slots[daySlot] && items[permutation[i]].slots[daySlot][next]) {
                    slot.next = angular.copy(items[permutation[i]].slots[daySlot][next]);
                    slot = slot.next;
                    next = next + items[permutation[i]].service.duration;
                } else {
                    return false;
                }
            }

            return true;
        }

        return false;
    };

    /***
     * @ngdoc method
     * @name findValidSlots
     * @methodOf BB.TimeSlotPermutationService
     * @description
     * finds valid time slot sequences where services can be performed back to back
     *
     * @param {array} items A array of BasketItems
     * @param {array} itemsSlots A Slot object
     * @param {int} maxGapBetweenSlots A string representing a date. Used for defining attribute for slots
     */
    service.findValidSlots = function (items, itemsSlots) {
        const daysBySlots = {};

        // splice the selected time back into the result
        service.addExistingDateTimes(items, itemsSlots);

        if (service.isItemSlotsValid(items, itemsSlots)) {

            // build days object, indexed by dates from the first set of slots
            // for (let [slotIndex, slot] of Object.entries(items[0].slots)) {
            for (const slotIndex in items[0].slots) {
                daysBySlots[slotIndex] = ({ date: moment(slotIndex) });
            }

            // check each sequence permutation for a set of valid start times where each service can performed back to back
            // find the permutations from the range given the number of items 
            const range = _.range(0, items.length);
            const permutations = service.permute(range);

            for (const daySlotIndex in daysBySlots) {

                daysBySlots[daySlotIndex].slots = {};

                if (items.length > 1) {

                    for (const permutationIndex in permutations) {

                        const permutation = permutations[permutationIndex];

                        if (_.isEmpty(items[permutation[0]].slots[daySlotIndex])) {
                            break;
                        }

                        for (const timeIndex in items[permutation[0]].slots[daySlotIndex]) {

                            const slot = angular.copy(items[permutation[0]].slots[daySlotIndex][timeIndex]);

                            // add the slot if it's valid and isn't already in the dataset
                            if (service.isItemsSlotPermutationValid(items, slot, daySlotIndex, permutation)) {
                                daysBySlots[daySlotIndex].slots[slot.time] = slot;
                            }
                        }
                    }
                } else {
                    // if there's just one basket item, just set the slots on that
                    for (const timeIndex in items[0].slots[daySlotIndex]) {
                        const slot = items[0].slots[daySlotIndex][timeIndex];
                        daysBySlots[daySlotIndex].slots[slot.time] = slot;
                    }
                }
            }
        }

        return daysBySlots;
    };
}

/***
 * @ngdoc service
 * @name BB.TimeSlotPermutationService
 * @requires DateTimeUtilitiesService
 * @description
 * handles finding a permutation of slots  for services to be carried out back to back
 * The permutations are found using the following conditions
 * - the order does not matter
 * - there are no repeating services / slots
 * - all services must have a slot (no services are left out from a permutation)
 */
angular
    .module('BB')
    .service('TimeSlotPermutationService', TimeSlotPermutationService);




// WEBPACK FOOTER //
// ../node_modules/bookingbug-angular/src/core/_services/time_slot_permutation/time_slot_permutation.service.js